<template>
  <div class="kpi-cards-container">
    <div class="kpi-card-wrapper" data-testid="pdv-entregas-kpi-today">
      <button
        type="button"
        class="kpi-card today-card kpi-card-clickable"
        :class="{ 'kpi-card-active': activeKpi === 'today' }"
        :aria-pressed="activeKpi === 'today'"
        data-testid="pdv-entregas-kpi-today-button"
        @click="emit('toggle-kpi', 'today')"
      >
        <div class="kpi-icon">
          <i class="pi pi-calendar"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Hoje e próximos 2 dias') }}</h4>
          <h2>{{ kpis.today_and_next_2_days ?? 0 }}</h2>
          <div class="kpi-trend">
            <i class="pi pi-clock"></i>
            <span>{{ t('A entregar nas próximas 72h') }}</span>
          </div>
        </div>
      </button>
    </div>

    <div class="kpi-card-wrapper" data-testid="pdv-entregas-kpi-delayed">
      <button
        type="button"
        class="kpi-card delayed-card kpi-card-clickable"
        :class="{ 'kpi-card-active': activeKpi === 'delayed' }"
        :aria-pressed="activeKpi === 'delayed'"
        data-testid="pdv-entregas-kpi-delayed-button"
        @click="emit('toggle-kpi', 'delayed')"
      >
        <div class="kpi-icon">
          <i class="pi pi-exclamation-triangle"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Atrasados') }}</h4>
          <h2>{{ kpis.delayed ?? 0 }}</h2>
          <div class="kpi-trend">
            <i class="pi pi-times-circle"></i>
            <span>{{ t('Prazo expirado') }}</span>
          </div>
        </div>
      </button>
    </div>

    <!-- Reagendados is the one non-clickable KPI: it counts items with a
         so_reprocessos row, and the items row contract carries no reprocesso
         field, so the list cannot be filtered by it. -->
    <div class="kpi-card-wrapper" data-testid="pdv-entregas-kpi-rescheduled">
      <div class="kpi-card rescheduled-card">
        <div class="kpi-icon">
          <i class="pi pi-history"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Reagendados') }}</h4>
          <h2>{{ kpis.rescheduled ?? 0 }}</h2>
          <div class="kpi-trend">
            <i class="pi pi-refresh"></i>
            <span>{{ t('Com reprocesso') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="kpi-card-wrapper" data-testid="pdv-entregas-kpi-alerts">
      <button
        type="button"
        class="kpi-card alerts-card kpi-card-clickable"
        :class="{ 'kpi-card-active': activeKpi === 'alerts' }"
        :aria-pressed="activeKpi === 'alerts'"
        data-testid="pdv-entregas-kpi-alerts-button"
        @click="emit('toggle-kpi', 'alerts')"
      >
        <div class="kpi-icon">
          <i class="pi pi-bell"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Alertas pendentes') }}</h4>
          <h2>{{ kpis.pending_alerts ?? 0 }}</h2>
          <div class="kpi-trend">
            <i class="pi pi-info-circle"></i>
            <span>{{ t('Cliente não foi avisado') }}</span>
          </div>
        </div>
      </button>
    </div>

    <div class="kpi-card-wrapper" data-testid="pdv-entregas-kpi-pending-delivery">
      <button
        type="button"
        class="kpi-card pending-delivery-card kpi-card-clickable"
        :class="{ 'kpi-card-active': activeKpi === 'pending-delivery' }"
        :aria-pressed="activeKpi === 'pending-delivery'"
        data-testid="pdv-entregas-kpi-pending-delivery-button"
        @click="emit('toggle-kpi', 'pending-delivery')"
      >
        <div class="kpi-icon">
          <i class="pi pi-box"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ t('Aguardando retirada') }}</h4>
          <h2>{{ kpis.pending_delivery ?? 0 }}</h2>
          <div class="kpi-trend">
            <i class="pi pi-arrow-right"></i>
            <span>{{ t('Aguardando retirada') }}</span>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Orders_EntregasKpisResponseDados } from '../../contracts/Orders_EntregasKpisResponse';
import type { EntregasKpiKey } from '../../composables/useEntregasKpiFilter';

const { t } = useI18n();

interface Props {
  kpis: Orders_EntregasKpisResponseDados;
  activeKpi?: EntregasKpiKey | null;
}

withDefaults(defineProps<Props>(), { activeKpi: null });

const emit = defineEmits<{ (e: 'toggle-kpi', key: EntregasKpiKey): void }>();
</script>

<style scoped>
/* KPI Cards Container - Flexbox Grid */
.kpi-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
}

.kpi-card-wrapper {
  flex: 1 1 calc(20% - 16px); /* 5 columns on large screens */
  min-width: 200px;
}

/* Responsive breakpoints */
@media (max-width: 1400px) {
  .kpi-card-wrapper {
    flex: 1 1 calc(33.333% - 14px); /* 3 columns on medium-large screens */
  }
}

@media (max-width: 1024px) {
  .kpi-card-wrapper {
    flex: 1 1 calc(50% - 10px); /* 2 columns on medium screens */
  }
}

@media (max-width: 768px) {
  .kpi-card-wrapper {
    flex: 1 1 100%; /* 1 column on small screens */
  }
}

.kpi-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 20px;
  height: 100%;
  transition: all 0.3s ease;
  border-left: 4px solid;
  position: relative;
  overflow: visible;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, currentColor, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Clickable cards are real buttons — strip the UA chrome, keep the card look. */
.kpi-card-clickable {
  width: 100%;
  font: inherit;
  color: inherit;
  text-align: left;
  border: none;
  border-left: 4px solid;
  cursor: pointer;
}

.kpi-card-clickable:focus-visible {
  outline: 2px solid var(--p-primary-color, #2196f3);
  outline-offset: 2px;
}

.kpi-card-active {
  background: var(--p-surface-100, #eef2f7);
  border-left-width: 8px;
  box-shadow: 0 0 0 2px currentColor inset;
}

.kpi-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12);
  transform: translateY(-3px);
}

.kpi-card:hover::before {
  opacity: 1;
}

/* Today + next 2 days — blue */
.today-card {
  border-left-color: #2196f3;
}

.today-card .kpi-icon {
  background: linear-gradient(135deg, #2196f3, #42a5f5);
}

/* Delayed — red */
.delayed-card {
  border-left-color: #f44336;
}

.delayed-card .kpi-icon {
  background: linear-gradient(135deg, #f44336, #ef5350);
}

/* Rescheduled — green */
.rescheduled-card {
  border-left-color: #4caf50;
}

.rescheduled-card .kpi-icon {
  background: linear-gradient(135deg, #4caf50, #66bb6a);
}

/* Alerts — amber/orange */
.alerts-card {
  border-left-color: #ff9800;
}

.alerts-card .kpi-icon {
  background: linear-gradient(135deg, #ff9800, #ffa726);
}

/* Pending delivery — purple */
.pending-delivery-card {
  border-left-color: #9c27b0;
}

.pending-delivery-card .kpi-icon {
  background: linear-gradient(135deg, #9c27b0, #ab47bc);
}

.kpi-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-content h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-content h2 {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.kpi-trend {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #888;
}

.kpi-trend i {
  font-size: 14px;
}
</style>
